import {
  CheckIcon,
  ChevronRightIcon,
  CopyIcon,
  PencilIcon,
  XIcon,
} from "lucide-react"
import { Fragment, useState } from "react"
import { useTranslation } from "react-i18next"
import { useNavigate } from "react-router-dom"

import {
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table"
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip"
import FRONTEND_ROUTES from "@/constants/front-end-routes"
import { formatMoney } from "@/helpers/format"
import { cn } from "@/lib/utils"
import type { SmartRoutingConfiguration } from "@/types/smart-routing"
import { calculateSmartRoutingAlerts } from "@/utils/smart-routing-alerts"

import { buttonVariants } from "../ui/button"
import { FailoverBadge } from "./failover-badge"
import { PaymentMethodBadge } from "./payment-method-badge"
import { ProgressIndicator } from "./progress-indicator"
import { SecondaryGateway } from "./secondary-gateway"
import { SmartRoutingStatusBadge } from "./status-badge"

export const TableHeaderContent = () => {
  const { t } = useTranslation()

  return (
    <TableHeader className="sticky top-0 z-20 h-10 w-full bg-background outline outline-1 outline-border">
      <TableRow>
        <TableHead className="lg:hidden">
          {t("smartRouting.table.mobileDetails")}
        </TableHead>
        <TableHead className="hidden w-[200px] lg:table-cell">
          {t("smartRouting.table.whiteLabel")}
        </TableHead>
        <TableHead className="hidden w-[180px] lg:table-cell">
          {t("smartRouting.table.paymentMethod")}
        </TableHead>
        <TableHead className="hidden w-[100px] lg:table-cell">
          {t("smartRouting.table.status")}
        </TableHead>
        <TableHead className="hidden w-[120px] lg:table-cell">
          {t("smartRouting.table.period")}
        </TableHead>
        <TableHead className="hidden w-[220px] lg:table-cell">
          {t("smartRouting.table.primary")}
        </TableHead>
        <TableHead className="hidden w-[220px] lg:table-cell">
          {t("smartRouting.table.secondary")}
        </TableHead>
        <TableHead className="hidden w-[120px] lg:table-cell">
          {t("smartRouting.table.failover")}
        </TableHead>
        <TableHead className="hidden w-[220px] lg:table-cell">
          {t("smartRouting.table.routingEnabled")}
        </TableHead>
        <TableHead className="w-[80px] text-center">
          {t("smartRouting.table.actions")}
        </TableHead>
      </TableRow>
    </TableHeader>
  )
}

type EmptyDataMessageProps = {
  colSpan: number
}

export const EmptyDataMessage = ({
  colSpan,
}: EmptyDataMessageProps) => {
  const { t } = useTranslation()

  return (
    <TableRow>
      <TableCell
        colSpan={colSpan}
        className="h-24 text-center"
      >
        {t("pages.smartRouting.noConfigurationsFound")}
      </TableCell>
    </TableRow>
  )
}

type MobileContentProps = {
  configuration: SmartRoutingConfiguration
}

export const MobileContent = ({
  configuration,
}: MobileContentProps) => {
  const { t } = useTranslation()

  const {
    whiteLabel,
    paymentMethod,
    statusDetail,
    primary,
    secondary,
    failoverRate,
    routing,
  } = configuration

  return (
    <TableCell className="p-4 lg:hidden">
      <div className="space-y-4">
        {/* Header com White Label, Método de Pagamento e Status */}
        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-semibold">
                {whiteLabel.name}
              </p>
              <p className="max-w-[220px] truncate font-mono text-xs text-muted-foreground">
                {whiteLabel.id}
              </p>
            </div>
            <SmartRoutingStatusBadge
              status={statusDetail.status}
            />
          </div>
          <div className="flex items-center gap-2">
            <PaymentMethodBadge
              paymentMethod={paymentMethod}
            />
            {configuration.period && (
              <span className="text-xs text-muted-foreground">
                {t(
                  `pages.smartRouting.form.periods.${configuration.period.toLowerCase()}`
                )}
              </span>
            )}
          </div>
        </div>

        {/* Gateway Primário */}
        <div className="space-y-1">
          <span className="text-xs font-medium uppercase text-muted-foreground">
            {t("smartRouting.table.primary")}
          </span>
          <ProgressIndicator
            processor={primary.processor}
            volume={primary.volume}
            target={primary.target}
            progress={primary.progress}
            fixed={primary.fixed}
          />
        </div>

        {/* Grid com informações secundárias */}
        <div className="grid grid-cols-2 gap-3">
          {/* Failover Rate */}
          <div className="space-y-1">
            <span className="text-xs font-medium uppercase text-muted-foreground">
              {t("smartRouting.table.failover")}
            </span>
            <FailoverBadge failoverRate={failoverRate} />
          </div>

          {/* Roteamento Ativo */}
          <div className="space-y-1">
            <span className="text-xs font-medium uppercase text-muted-foreground">
              {t("smartRouting.table.routingEnabled")}
            </span>
            <div className="flex items-center gap-2">
              <div
                className={cn(
                  "flex h-5 w-5 items-center justify-center rounded-full",
                  routing.enabled
                    ? "bg-green-100 text-green-700"
                    : "bg-muted text-muted-foreground"
                )}
              >
                {routing.enabled ? (
                  <CheckIcon size={12} />
                ) : (
                  <XIcon size={12} />
                )}
              </div>
              <span className="text-sm">
                {routing.enabled
                  ? t("common.yes")
                  : t("common.no")}
              </span>
            </div>
          </div>
        </div>

        {/* Gateway Secundário */}
        {secondary && (
          <div className="space-y-1">
            <span className="text-xs font-medium uppercase text-muted-foreground">
              {t("smartRouting.table.secondary")}
            </span>
            <SecondaryGateway
              secondary={secondary}
              primaryProgress={primary.progress}
            />
          </div>
        )}
      </div>
    </TableCell>
  )
}

type DesktopContentProps = {
  configuration: SmartRoutingConfiguration
  isExpanded: boolean
  setIsExpanded: (expanded: boolean) => void
  canExpand: boolean
}

export const DesktopContent = ({
  configuration,
  isExpanded,
  setIsExpanded,
  canExpand,
}: DesktopContentProps) => {
  const { t } = useTranslation()
  const {
    whiteLabel,
    paymentMethod,
    statusDetail,
    primary,
    secondary,
    failoverRate,
    routing,
  } = configuration

  return (
    <>
      {/* White Label - Desktop */}
      <TableCell className="hidden w-[200px] max-w-[200px] align-top lg:table-cell">
        <div className="min-w-0 space-y-1">
          <div
            className={cn(
              "flex items-center gap-1.5",
              canExpand && "cursor-pointer"
            )}
            onClick={() =>
              canExpand && setIsExpanded(!isExpanded)
            }
          >
            <ChevronRightIcon
              size={14}
              className={cn(
                "shrink-0 transition-transform duration-200",
                canExpand
                  ? "text-muted-foreground"
                  : "text-transparent",
                isExpanded && "rotate-90"
              )}
            />
            <p className="truncate font-semibold">
              {whiteLabel.name}
            </p>
          </div>
          <div className="flex items-center gap-1 pl-5">
            <p className="truncate font-mono text-xs text-muted-foreground">
              {whiteLabel.id}
            </p>
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation()
                navigator.clipboard.writeText(whiteLabel.id)
              }}
              className="shrink-0 rounded p-0.5 text-muted-foreground hover:bg-muted hover:text-foreground"
              aria-label={t("common.copy")}
            >
              <CopyIcon size={12} />
            </button>
          </div>
        </div>
      </TableCell>

      {/* Método de Pagamento - Desktop */}
      <TableCell className="hidden align-top lg:table-cell">
        <PaymentMethodBadge paymentMethod={paymentMethod} />
      </TableCell>

      {/* Status - Desktop */}
      <TableCell className="hidden align-top lg:table-cell">
        <SmartRoutingStatusBadge
          status={statusDetail.status}
        />
      </TableCell>

      {/* Período - Desktop */}
      <TableCell className="hidden align-top lg:table-cell">
        <span className="text-sm">
          {configuration.period
            ? t(
                `pages.smartRouting.form.periods.${configuration.period.toLowerCase()}`
              )
            : "—"}
        </span>
      </TableCell>

      {/* Gateway Primário - Desktop */}
      <TableCell className="hidden align-top lg:table-cell">
        <ProgressIndicator
          processor={primary.processor}
          volume={primary.volume}
          target={primary.target}
          progress={primary.progress}
          fixed={primary.fixed}
        />
      </TableCell>

      {/* Gateway Secundário - Desktop */}
      <TableCell className="hidden align-top lg:table-cell">
        <SecondaryGateway
          secondary={secondary}
          primaryProgress={primary.progress}
        />
      </TableCell>

      {/* Failover Rate - Desktop */}
      <TableCell className="hidden align-top lg:table-cell">
        <FailoverBadge failoverRate={failoverRate} />
      </TableCell>

      {/* Roteamento Ativo - Desktop */}
      <TableCell className="hidden align-top lg:table-cell">
        <div
          className={cn(
            "flex h-6 w-6 items-center justify-center rounded-full",
            routing.enabled
              ? "bg-green-100 text-green-700"
              : "bg-muted text-muted-foreground"
          )}
        >
          {routing.enabled ? (
            <CheckIcon size={13} />
          ) : (
            <XIcon size={13} />
          )}
        </div>
      </TableCell>
    </>
  )
}

type SmartRoutingRowProps = {
  configuration: SmartRoutingConfiguration
}

const SmartRoutingRow = ({
  configuration,
}: SmartRoutingRowProps) => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const [isExpanded, setIsExpanded] = useState(false)
  const canExpand = !configuration.primary.fixed
  const alerts = calculateSmartRoutingAlerts(configuration)

  const handleEdit = () => {
    navigate(
      FRONTEND_ROUTES.SMART_ROUTING_CONFIGURATION_EDIT.href.replace(
        ":uuid",
        configuration.id
      )
    )
  }

  return (
    <Fragment>
      <TableRow className="even:bg-zinc-100 even:hover:bg-zinc-100">
        <MobileContent configuration={configuration} />
        <DesktopContent
          configuration={configuration}
          isExpanded={isExpanded}
          setIsExpanded={setIsExpanded}
          canExpand={canExpand}
        />

        {/* Ações - Sempre visível */}
        <TableCell className="align-top">
          <div className="flex items-center justify-center">
            <Tooltip>
              <TooltipTrigger asChild>
                <button
                  type="button"
                  onClick={handleEdit}
                  className={cn(
                    buttonVariants({
                      size: "icon",
                      variant: "ghost",
                    }),
                    "h-8 w-8"
                  )}
                  aria-label={t("common.edit")}
                >
                  <PencilIcon size={16} />
                </button>
              </TooltipTrigger>
              <TooltipContent
                style={{
                  backgroundColor:
                    "var(--whitelabel-background)",
                }}
                className="border-0 text-white"
              >
                {t("common.edit")}
              </TooltipContent>
            </Tooltip>
          </div>
        </TableCell>
      </TableRow>

      {/* Linha expandida - detalhes adicionais */}
      {isExpanded && canExpand && (
        <TableRow className="bg-muted/30">
          <TableCell
            colSpan={10}
            className="hidden p-4 lg:table-cell"
          >
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
              {/* Volume Detalhado */}
              <div className="space-y-2">
                <p className="text-xs font-medium uppercase text-muted-foreground">
                  {t(
                    "smartRouting.expandedRow.volumeDetails"
                  )}
                </p>
                <div className="space-y-1 text-sm">
                  <p>
                    <span className="font-medium">
                      {t(
                        "smartRouting.expandedRow.processed"
                      )}
                      :
                    </span>{" "}
                    {configuration.primary.volume
                      ? formatMoney(
                          configuration.primary.volume / 100
                        )
                      : "—"}
                  </p>
                  <p>
                    <span className="font-medium">
                      {t("smartRouting.expandedRow.target")}
                      :
                    </span>{" "}
                    {configuration.primary.target
                      ? formatMoney(
                          configuration.primary.target / 100
                        )
                      : "—"}
                  </p>
                </div>
              </div>

              {/* Configurações de Roteamento */}
              <div className="space-y-2">
                <p className="text-xs font-medium uppercase text-muted-foreground">
                  {t(
                    "smartRouting.expandedRow.routingConfig"
                  )}
                </p>
                <div className="space-y-1 text-sm">
                  <p className="flex items-center gap-2">
                    <span className="font-medium">
                      {t(
                        "smartRouting.expandedRow.byVolume"
                      )}
                      :
                    </span>
                    {configuration.routing.byVolume ? (
                      <span className="text-green-600">
                        ✅ {t("common.yes")}
                      </span>
                    ) : (
                      <span className="text-muted-foreground">
                        ❌ {t("common.no")}
                      </span>
                    )}
                  </p>
                  <p className="flex items-center gap-2">
                    <span className="font-medium">
                      {t(
                        "smartRouting.expandedRow.priorityAfterTarget"
                      )}
                      :
                    </span>
                    {configuration.routing
                      .priorityAfterTarget ? (
                      <span className="text-green-600">
                        ✅ {t("common.yes")}
                      </span>
                    ) : (
                      <span className="text-muted-foreground">
                        ❌ {t("common.no")}
                      </span>
                    )}
                  </p>
                </div>
              </div>

              {/* Alertas e Status */}
              {(alerts.hasFailoverAlert ||
                alerts.hasVolumeAlert) && (
                <div className="space-y-2">
                  <p className="text-xs font-medium uppercase text-red-600">
                    {t("smartRouting.expandedRow.alerts")}
                  </p>
                  <div className="space-y-1 text-sm">
                    {alerts.hasFailoverAlert && (
                      <p className="text-red-600">
                        🚨{" "}
                        {t(
                          "smartRouting.alerts.failoverHigh"
                        )}
                      </p>
                    )}
                    {alerts.hasVolumeAlert && (
                      <p className="text-amber-600">
                        ⚠️{" "}
                        {t(
                          "smartRouting.alerts.volumeExceeded"
                        )}
                      </p>
                    )}
                  </div>
                </div>
              )}
            </div>
          </TableCell>
        </TableRow>
      )}
    </Fragment>
  )
}

type TableContentProps = {
  data: SmartRoutingConfiguration[] | undefined
}

export const TableContent = ({
  data,
}: TableContentProps) => {
  if (!data || data.length === 0) {
    return <EmptyDataMessage colSpan={10} />
  }

  return (
    <>
      {data.map((configuration) => (
        <SmartRoutingRow
          key={configuration.id}
          configuration={configuration}
        />
      ))}
    </>
  )
}
